import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import RichTextEditor from '@/components/RichTextEditor';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Save, Eye, ArrowLeft, Upload, Image, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BlogEditorPage = () => {
  const { blogId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const isEdit = !!blogId;

  const [form, setForm] = useState({
    title: '', content: '', excerpt: '', cover_image: '',
    category: 'general', tags: '', status: 'draft',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const coverInputRef = React.useRef(null);

  useEffect(() => {
    if (isEdit) {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      axios.get(`${API}/blogs/${blogId}`, { headers, withCredentials: true })
        .then(res => {
          const b = res.data;
          setForm({
            title: b.title || '',
            content: b.content || '',
            excerpt: b.excerpt || '',
            cover_image: b.cover_image || '',
            category: b.category || 'general',
            tags: (b.tags || []).join(', '),
            status: b.status || 'draft',
          });
        })
        .catch(() => toast.error('Failed to load blog'));
    }
  }, [blogId, isEdit, token]);

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload JPG, PNG, WebP, or GIF image'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB'); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' };
      const res = await axios.post(`${API}/blogs/upload-cover`, fd, { headers, withCredentials: true, timeout: 30000 });
      setForm(f => ({ ...f, cover_image: res.data.url }));
      toast.success('Cover image uploaded!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    }
    setUploading(false);
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  const handleSubmit = async (overrideStatus) => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.content.trim()) { toast.error('Content is required'); return; }

    setSaving(true);
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
    const payload = {
      ...form,
      tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
      status: overrideStatus || form.status,
    };

    try {
      if (isEdit) {
        await axios.put(`${API}/blogs/${blogId}`, payload, { headers, withCredentials: true });
        toast.success('Blog updated');
      } else {
        await axios.post(`${API}/blogs`, payload, { headers, withCredentials: true });
        toast.success('Blog created');
      }
      navigate('/admin');
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="blog-editor">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              {isEdit ? 'Edit Post' : 'New Post'}
            </h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleSubmit('draft')} disabled={saving} data-testid="save-draft-btn">
              <Save className="w-4 h-4 mr-2" />Draft
            </Button>
            <Button onClick={() => handleSubmit('published')} disabled={saving} data-testid="publish-btn">
              <Eye className="w-4 h-4 mr-2" />Publish
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-6 space-y-5">
            <div>
              <Label>Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Article title..."
                className="mt-1 text-lg"
                data-testid="blog-title-input"
              />
            </div>

            <div>
              <Label>Excerpt</Label>
              <Input
                value={form.excerpt}
                onChange={(e) => setForm(f => ({ ...f, excerpt: e.target.value }))}
                placeholder="Short description..."
                className="mt-1"
                data-testid="blog-excerpt-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger className="mt-1" data-testid="blog-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="news">News</SelectItem>
                    <SelectItem value="career-tips">Career Tips</SelectItem>
                    <SelectItem value="education">Education</SelectItem>
                    <SelectItem value="industry">Industry</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tags (comma separated)</Label>
                <Input
                  value={form.tags}
                  onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))}
                  placeholder="e.g. teaching, career"
                  className="mt-1"
                  data-testid="blog-tags-input"
                />
              </div>
            </div>

            <div>
              <Label>Cover Image (1200 x 628 px recommended)</Label>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleCoverUpload}
                className="hidden"
                id="cover-upload-input"
                data-testid="blog-cover-file-input"
              />
              {form.cover_image ? (
                <div className="mt-2 relative group rounded-lg overflow-hidden border border-slate-200">
                  <img src={form.cover_image.startsWith('/api') ? `${API.replace('/api', '')}${form.cover_image}` : form.cover_image} alt="Cover" className="w-full h-40 object-cover" />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                    <Button size="sm" variant="secondary" onClick={() => document.getElementById('cover-upload-input').click()} data-testid="blog-cover-replace-btn">
                      <Upload className="w-3.5 h-3.5 mr-1.5" /> Replace
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => setForm(f => ({ ...f, cover_image: '' }))} data-testid="blog-cover-remove-btn">
                      <X className="w-3.5 h-3.5 mr-1.5" /> Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="mt-2 border-2 border-dashed border-slate-200 rounded-lg p-8 text-center cursor-pointer hover:border-indigo-300 hover:bg-slate-50 transition-colors"
                  onClick={() => document.getElementById('cover-upload-input').click()}
                  data-testid="blog-cover-upload-area"
                >
                  {uploading ? (
                    <div className="space-y-2">
                      <Loader2 className="w-8 h-8 text-indigo-600 mx-auto animate-spin" />
                      <p className="text-sm text-slate-600">Uploading...</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Image className="w-8 h-8 text-slate-300 mx-auto" />
                      <p className="text-sm text-slate-600 font-medium">Click to upload cover image</p>
                      <p className="text-xs text-slate-400">JPG, PNG, WebP, GIF — max 5MB — 1200x628px recommended</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label>Content</Label>
              <div className="mt-1">
                <RichTextEditor
                  value={form.content}
                  onChange={(html) => setForm(f => ({ ...f, content: html }))}
                  token={token}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default BlogEditorPage;
